/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › DLP (docs/CONTRACT-fleet-governance.md sections 7.3, 9.1 and 12.3): per kind, app and detector, what DLP
 * blocked, redacted and audited and how many hits, with a sparkline over the range, from GET /v1/admin/governance/dlp
 * (sorted and paged by the service). An admin's deny-list term is never shown: it reads "Deny list term". A row opens its
 * audit rows (`egress.dlp_` or `dlp.write_`, that app).
 */
import { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { sortParam, toggleSort, type SortState } from '../../lib/fleet';
import { DLP_CHOICES, DLP_KIND_LABELS, detectorLabel, trendsApi, trendsExportHref, type GovDlpAnswer, type GovDlpRow } from '../../lib/trends';
import { EmptyState, Panel, Sparkline, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { usePagedList } from '../pulse/common';
import { ExportLink } from '../fleet/parts';
import { SectionError, type SectionProps } from './parts';

const DEFAULT_SORT: SortState = { key: 'blocked', direction: 'desc' };
const WORD_COLUMNS = new Set(['app', 'detector']);

export function TrendsDlp({ query, filter, onShowRows, onApps }: SectionProps & { onApps?: (apps: readonly string[]) => void }) {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const params = { ...query, sort: sortParam(sort) };
  const list = usePagedList((cursor) => trendsApi.dlp({ ...params, cursor, limit: 50 }), `dlp|${JSON.stringify(params)}`);
  const answer = list.load.state === 'ready' ? (list.load.data as GovDlpAnswer) : null;
  const items = list.items;
  // The apps this section reports feed the page's app filter (an auditor cannot read the registered apps).
  const appKey = [...new Set(items.map((row) => row.app))].join('\n');
  useEffect(() => {
    if (onApps && appKey) onApps(appKey.split('\n'));
  }, [appKey, onApps]);

  const open = (row: GovDlpRow) => {
    if (!answer) return;
    onShowRows({
      title: `DLP · ${DLP_KIND_LABELS[row.kind]} · ${row.app} · ${detectorLabel(row.detector)}`,
      choices: [DLP_CHOICES[row.kind]],
      app: row.app,
      range: { from: answer.range.from, to: answer.range.to },
    });
  };

  const columns: Array<Column<GovDlpRow>> = [
    { key: 'kind', header: 'Kind', cell: (row) => DLP_KIND_LABELS[row.kind] ?? row.kind },
    { key: 'app', header: 'App', sortable: true, cell: (row) => <Tag>{row.app}</Tag> },
    { key: 'detector', header: 'Detector', sortable: true, cell: (row) => (row.detector === 'deny' || row.detector === 'other' ? detectorLabel(row.detector) : <Tag>{row.detector}</Tag>) },
    { key: 'blocked', header: 'Blocked', numeric: true, sortable: true, cell: (row) => count(row.blocked) },
    { key: 'redacted', header: 'Redacted', numeric: true, sortable: true, cell: (row) => count(row.redacted) },
    { key: 'audited', header: 'Audited', numeric: true, hideOnPhone: true, cell: (row) => count(row.audited) },
    { key: 'hits', header: 'Hits', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => count(row.hits) },
    { key: 'spark', header: 'Per day', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${row.app} ${detectorLabel(row.detector)}: blocked and redacted per day`} tone={row.blocked > 0 ? 'bad' : 'data'} /> },
  ];

  const footer = answer && (answer.unparsed > 0 || answer.truncated > 0) ? (
    <span>
      {answer.unparsed > 0 ? `Rows the parser could not read: ${count(answer.unparsed)}.` : null}
      {answer.unparsed > 0 && answer.truncated > 0 ? ' ' : null}
      {answer.truncated > 0 ? `Entries left out of long audit details: ${count(answer.truncated)}.` : null}
    </span>
  ) : undefined;

  return (
    <Panel flush title="DLP" description="What data loss prevention blocked, redacted and audited, by app and detector" actions={<ExportLink href={trendsExportHref('dlp', filter)} />} footer={footer}>
      {list.load.state === 'failed' ? <div className="ui-panel-pad"><SectionError title="The DLP counts could not be loaded" error={list.load.error} onRetry={list.reload} retrying={list.reloading} /></div> : (
        <Table
          label="DLP"
          columns={columns}
          rows={items}
          rowKey={(row) => `${row.kind}|${row.app}|${row.detector}`}
          loading={list.load.state === 'loading'}
          skeletonRows={4}
          onRowClick={open}
          sort={{ key: sort.key, direction: sort.direction, onSort: (key) => setSort(toggleSort(sort, key, !WORD_COLUMNS.has(key))) }}
          more={{ cursor: list.next, loading: list.moreLoading, onMore: list.loadMore }}
          empty={<EmptyState compact icon={ShieldCheck} title="Nothing blocked, redacted or audited">DLP counts appear here as answers, sources and tool writes pass the DLP rules.</EmptyState>}
        />
      )}
      {list.moreError ? <div className="ui-panel-pad ui-panel-pad--top"><SectionError title="The next page could not be loaded" error={list.moreError} /></div> : null}
    </Panel>
  );
}
